/**
 * L'export des données — ce que le patron emporte en partant.
 *
 * Un seul verbe, réservé au patron : l'archive contient les coûts horaires
 * des salariés, l'IBAN et tout le commercial. Il lit, il n'écrit rien —
 * niveau N1 —, mais il est journalisé quand même, ce que `defineService` ne
 * fait pas d'office pour une lecture : sortir **toutes** les données de
 * l'entreprise d'un coup n'est pas une consultation comme une autre.
 *
 * Le service assemble l'archive fichier par fichier, sans la compresser :
 * l'écriture du `.zip` et sa remise au navigateur appartiennent à l'écran.
 */
import { ActionLevel } from "../../levels.js";
import { DomainError } from "../../errors.js";
import { defineService } from "../../service.js";
import type { ExecutionContext } from "../../context.js";
import type { ServiceInput } from "../../service.js";
import { EXPORTED_TABLES, exportKey } from "./tables.js";
import { projectSlug } from "../photos/types.js";
import {
  documentEntryName,
  documentNumbers,
  exportArchiveName,
  manifestJson,
  readmeText,
  redactRow,
  toCsv,
  toJson,
  uniqueNames,
} from "./format.js";
import type { ExportRow, ManifestFile, ManifestTable } from "./format.js";

/** Les buckets d'où l'export télécharge. */
export type ExportBucket = "documents" | "branding" | "chantier-documents" | "equipment-photos";

/** Ce dont l'export a besoin de la base et du Storage. */
export interface ExportRepository {
  /**
   * Toutes les lignes d'une table pour l'entreprise de `ctx`, dans l'ordre de
   * `key`. Toutes : une implémentation qui s'arrêterait à la première page
   * rendrait une archive incomplète qui aurait l'air complète.
   */
  readTable(ctx: ExecutionContext, table: string, key: readonly string[]): Promise<ExportRow[]>;
  /** Les chemins de tous les objets de l'entreprise dans un bucket. */
  listObjects(ctx: ExecutionContext, bucket: "documents" | "branding"): Promise<string[]>;
  /** Le contenu d'un objet, ou `null` si le Storage ne le rend pas. */
  download(
    ctx: ExecutionContext,
    bucket: ExportBucket,
    path: string,
  ): Promise<Uint8Array | null>;
}

/**
 * Ce que le noyau ne sait pas faire seul : il ne connaît que l'ES2022, sans
 * `TextEncoder` ni `crypto`. Le navigateur les fournit.
 */
export interface ExportTools {
  encode(text: string): Uint8Array;
  sha256(data: Uint8Array): Promise<string>;
  now?: () => Date;
}

export interface ExportFile {
  /** Chemin dans l'archive. */
  path: string;
  data: Uint8Array;
}

export interface ExportArchive {
  fileName: string;
  files: ExportFile[];
  totalRows: number;
  documents: number;
  /** Documents listés mais non rendus. Comptés, jamais tus. */
  missingDocuments: string[];
}

export type ExportDataInput = ServiceInput;

/** Téléchargements simultanés depuis le Storage. */
const DOWNLOAD_CONCURRENCY = 4;

export function createExportServices(repo: ExportRepository, tools: ExportTools) {
  const exporterDonnees = defineService<ExportDataInput, ExportArchive>({
    name: "exporter_donnees",
    level: ActionLevel.Read,
    allowedRoles: ["patron"],
    handler: async (ctx, _input, deps) => {
      const generatedAt = tools.now?.() ?? new Date();

      // ── Les tables ─────────────────────────────────────────────────────
      // Une à une : quarante requêtes de front sur une connexion de chantier
      // échoueraient plus souvent qu'elles n'iraient vite.
      const rowsByTable = new Map<string, ExportRow[]>();
      for (const [table, spec] of Object.entries(EXPORTED_TABLES)) {
        const rows = await repo.readTable(ctx, table, exportKey(spec));
        rowsByTable.set(table, rows.map((row) => redactRow(table, row)));
      }

      const tenantRow = rowsByTable.get("tenants")?.[0];
      if (!tenantRow) {
        throw new DomainError("not_found", "La fiche de votre entreprise est illisible : export impossible.");
      }
      const tenantName = typeof tenantRow.name === "string" ? tenantRow.name : "entreprise";

      const files: ExportFile[] = [];
      const tables: ManifestTable[] = [];
      for (const [table, spec] of Object.entries(EXPORTED_TABLES)) {
        const rows = rowsByTable.get(table) ?? [];
        tables.push({ table, folder: spec.folder, rows: rows.length });
        files.push(
          { path: `${spec.folder}/${table}.csv`, data: tools.encode(toCsv(rows)) },
          { path: `${spec.folder}/${table}.json`, data: tools.encode(toJson(rows)) },
        );
      }

      // ── Les documents ──────────────────────────────────────────────────
      const numbers = documentNumbers(
        rowsByTable.get("quotes") ?? [],
        rowsByTable.get("invoices") ?? [],
      );
      const documentPaths = await repo.listObjects(ctx, "documents");
      const logoPaths = await repo.listObjects(ctx, "branding");

      const wanted = [
        ...documentPaths.map((path) => ({
          bucket: "documents" as const,
          path,
          name: documentEntryName(path, ctx.tenantId, numbers),
        })),
        ...logoPaths.map((path) => ({
          bucket: "branding" as const,
          path,
          name: `entreprise/${path.split("/").pop() ?? "logo"}`,
        })),
        // Les documents de chantier suivent leurs lignes, pas le contenu du
        // bucket : un document retiré par le patron ne revient pas dans
        // l'archive. Rangés par chantier, sous leur nom d'origine.
        ...siteDocumentEntries(rowsByTable.get("project_documents") ?? [], rowsByTable.get("projects") ?? []),
        // La photo de chaque machine, sous le nom de la machine.
        ...equipmentPhotoEntries(rowsByTable.get("equipment") ?? []),
      ];
      const names = uniqueNames(wanted.map((w) => w.name));
      const downloaded = new Array<ExportFile | null>(wanted.length).fill(null);

      await forEachLimited(wanted, DOWNLOAD_CONCURRENCY, async (item, index) => {
        const data = await repo.download(ctx, item.bucket, item.path);
        if (data) downloaded[index] = { path: names[index]!, data };
      });

      const missingDocuments = wanted
        .filter((_, index) => downloaded[index] === null)
        .map((item) => item.path);
      const documents = downloaded.filter((file): file is ExportFile => file !== null);
      files.push(...documents);

      // ── LISEZMOI et manifeste ──────────────────────────────────────────
      const totalRows = tables.reduce((sum, t) => sum + t.rows, 0);
      files.unshift({
        path: "LISEZMOI.txt",
        data: tools.encode(
          readmeText({ tenantName, generatedAt, totalRows, documents: documents.length }),
        ),
      });

      const manifestFiles: ManifestFile[] = [];
      for (const file of files) {
        manifestFiles.push({
          path: file.path,
          bytes: file.data.length,
          sha256: await tools.sha256(file.data),
        });
      }
      files.push({
        path: "manifeste.json",
        data: tools.encode(
          manifestJson({
            generatedAt,
            tenant: { id: ctx.tenantId, name: tenantName },
            tables,
            files: manifestFiles,
            missingDocuments,
          }),
        ),
      });

      // Une lecture n'est pas journalisée d'office. Celle-ci l'est : c'est la
      // sortie de toutes les données de l'entreprise.
      await deps.audit?.record({
        ctx,
        action: "exporter_donnees",
        level: ActionLevel.Read,
        result: "ok",
        details: {
          lignes: totalRows,
          documents: documents.length,
          documents_manquants: missingDocuments.length,
        },
      });

      return {
        fileName: exportArchiveName(tenantName, generatedAt),
        files,
        totalRows,
        documents: documents.length,
        missingDocuments,
      };
    },
  });

  return { exporterDonnees };
}

export type ExportServices = ReturnType<typeof createExportServices>;

function siteDocumentEntries(documents: readonly ExportRow[], projects: readonly ExportRow[]) {
  const projectName = new Map(
    projects.map((p) => [String(p.id), typeof p.name === "string" ? p.name : "chantier"]),
  );
  return documents
    .filter((d) => typeof d.storage_path === "string" && d.deleted_at == null)
    .map((d) => ({
      bucket: "chantier-documents" as const,
      path: d.storage_path as string,
      name:
        `chantiers/documents/${projectSlug(projectName.get(String(d.project_id)) ?? "chantier").toLowerCase()}/` +
        `${String(d.file_name ?? "document").replace(/[\\/:*?"<>|]+/g, "-")}`,
    }));
}

function equipmentPhotoEntries(equipment: readonly ExportRow[]) {
  return equipment
    .filter((e) => typeof e.photo_path === "string")
    .map((e) => {
      const path = e.photo_path as string;
      const ext = path.includes(".") ? path.slice(path.lastIndexOf(".")) : "";
      return {
        bucket: "equipment-photos" as const,
        path,
        name: `materiel/photos/${projectSlug(typeof e.name === "string" ? e.name : "machine").toLowerCase()}${ext}`,
      };
    });
}

async function forEachLimited<T>(
  items: readonly T[],
  limit: number,
  run: (item: T, index: number) => Promise<void>,
): Promise<void> {
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const index = next++;
      await run(items[index]!, index);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
}
